import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR } from "@/components/app/Shared";
import LiveFeed from "@/components/app/LiveFeed";
import { Shield, LogOut, AlertTriangle, ShieldCheck, Users, Activity, KeyRound, Globe, ChevronDown, ChevronRight, Monitor, Smartphone, Laptop, FileSearch, UserX, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function CyberDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [security, setSecurity] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [docLog, setDocLog] = useState([]);
  const [roleAudit, setRoleAudit] = useState([]);

  const load = async () => {
    try {
      const [s, o, e, ss, dl, ra] = await Promise.all([
        api.get("/cyber/security-events"),
        api.get("/cyber/org-analytics"),
        api.get("/cyber/login-events?limit=100"),
        api.get("/cyber/active-sessions"),
        api.get("/cyber/document-access-log"),
        api.get("/cyber/role-audit-log"),
      ]);
      setSecurity(s.data); setOrgs(o.data); setEvents(e.data);
      setSessions(ss.data); setDocLog(dl.data); setRoleAudit(ra.data);
    } catch { toast.error("Laden mislukt"); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const forceLogout = async (uid, name) => {
    if (!window.confirm(`Force logout: ${name}? Hun huidige sessie wordt onmiddellijk ongeldig.`)) return;
    try {
      await api.post(`/cyber/force-logout/${uid}`);
      toast.success(`${name} uitgelogd`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force logout mislukt");
    }
  };

  // Security score: green if 2FA>=50% en geen alerts; amber if some alerts; red if high-severity alerts
  const securityScore = (() => {
    if (!security) return { color: "zinc", label: "—", score: 0 };
    const highAlerts = security.alerts.filter((a) => a.severity === "high").length;
    const twofa = security.twofa_adoption_pct;
    if (highAlerts > 0) return { color: "red", label: "Kritiek", score: 30 };
    if (security.alerts.length > 0 || twofa < 25) return { color: "amber", label: "Aandacht", score: 60 };
    if (twofa >= 50) return { color: "emerald", label: "Beveiligd", score: 95 };
    return { color: "emerald", label: "Goed", score: 80 };
  })();

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="cyber-dashboard">
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={20} className="text-purple-600" />
          <div className="font-bold tracking-tight">Stockly · Cybersecurity</div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-500 hidden sm:inline">{user?.name}</span>
          <button onClick={async () => { await logout(); nav("/login"); }} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900" data-testid="logout-button">
            <LogOut size={15} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
        <PageHeader
          overline="Security Center"
          title="Cybersecurity overzicht"
          description="Detecteer verdachte logins, monitor 2FA adoptie en bekijk organisatie-activiteit."
        />

        {/* KPIs */}
        {security && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Kpi icon={ShieldCheck}    label="Security score" value={`${securityScore.score}/100`} accent={securityScore.color === "red" ? "danger" : securityScore.color === "amber" ? "warn" : "ok"} sub={securityScore.label} />
            <Kpi icon={AlertTriangle} label="Alerts (24u)"        value={security.alerts.length}             accent={security.alerts.length > 0 ? "danger" : "ok"} />
            <Kpi icon={Users}          label="Active sessions"     value={sessions.length} />
            <Kpi icon={ShieldCheck}    label="2FA adoptie"         value={`${security.twofa_adoption_pct}%`} accent={security.twofa_adoption_pct < 50 ? "warn" : "ok"} />
            <Kpi icon={Activity}       label="Logins (24u)"        value={`${security.total_logins_24h - security.failed_logins_24h}/${security.total_logins_24h}`} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview">Security Center</TabBtn>
          <TabBtn active={tab === "sessions"} onClick={() => setTab("sessions")} testid="tab-sessions">Active sessions</TabBtn>
          <TabBtn active={tab === "orgs"}     onClick={() => setTab("orgs")}     testid="tab-orgs">Organisaties</TabBtn>
          <TabBtn active={tab === "events"}   onClick={() => setTab("events")}   testid="tab-events">Login events</TabBtn>
          <TabBtn active={tab === "docs"}     onClick={() => setTab("docs")}     testid="tab-docs">Documenten</TabBtn>
          <TabBtn active={tab === "audit"}    onClick={() => setTab("audit")}    testid="tab-audit">Role audit</TabBtn>
          <TabBtn active={tab === "feed"}     onClick={() => setTab("feed")}     testid="tab-feed">Live feed</TabBtn>
        </div>

        {tab === "overview" && security && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 card-flat p-5">
              <div className="flex items-center gap-2 mb-4"><AlertTriangle size={16} className="text-amber-600" /><h3 className="font-bold tracking-tight text-[#1B2D5C]">Suspicious activity</h3></div>
              {security.alerts.length === 0 ? (
                <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">Geen verdachte activiteit gedetecteerd in de laatste 24 uur. ✓</div>
              ) : (
                <div className="space-y-2">
                  {security.alerts.map((a, i) => (
                    <div key={i} className={`p-3 rounded-md border ${a.severity === "high" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid={`alert-${a.type}`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={15} className={a.severity === "high" ? "text-red-600 mt-0.5" : "text-amber-600 mt-0.5"} />
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-[#1B2D5C]">{a.title}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">Severity: {a.severity} · Type: {a.type}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex items-center gap-2 mb-3"><Globe size={16} className="text-blue-600" /><h3 className="font-bold tracking-tight text-[#1B2D5C]">Top IP's (24u)</h3></div>
              <div className="space-y-1">
                {security.top_ips.map((row, i) => (
                  <div key={i} className="flex justify-between py-1.5 px-2 hover:bg-zinc-50 rounded text-sm">
                    <code className="font-mono text-zinc-700">{row.ip}</code>
                    <span className="font-mono font-semibold text-[#1B2D5C]">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-flat p-5">
              <div className="flex items-center gap-2 mb-4"><KeyRound size={16} className="text-emerald-600" /><h3 className="font-bold tracking-tight text-[#1B2D5C]">2FA adoptie</h3></div>
              <div className="text-4xl font-bold tracking-tight text-[#1B2D5C] font-mono">{security.twofa_adoption_pct}%</div>
              <div className="text-sm text-zinc-500 mt-1">{security.twofa_enabled_users} van {security.total_users} accounts gebruiken 2FA</div>
              <div className="mt-4 h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${security.twofa_adoption_pct}%` }} />
              </div>
              <div className="mt-5 text-xs text-zinc-500 leading-relaxed border-t border-zinc-100 pt-4">
                <b className="text-[#1B2D5C]">Aanbeveling:</b> Verplicht 2FA voor super-admin en alle organisatie-admins. Gebruikers activeren 2FA via <code className="bg-zinc-100 px-1 rounded">Profiel → Beveiliging</code>.
              </div>
            </div>
          </div>
        )}

        {tab === "orgs" && (
          <div className="space-y-3">
            {orgs.map((o) => <OrgAnalyticsCard key={o.id} org={o} />)}
            {orgs.length === 0 && <div className="card-flat p-8 text-center text-sm text-zinc-500">Nog geen organisaties met activiteit deze week.</div>}
          </div>
        )}

        {tab === "events" && (
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Wanneer</th>
                  <th className="px-4 py-3 overline">E-mail</th>
                  <th className="px-4 py-3 overline">Status</th>
                  <th className="px-4 py-3 overline">IP</th>
                  <th className="px-4 py-3 overline">Apparaat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {events.map((e) => (
                  <tr key={e.id} data-testid={`event-${e.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(e.created_at).toLocaleString("nl-NL")}</td>
                    <td className="px-4 py-3 text-zinc-700 font-mono text-xs">{e.email}</td>
                    <td className="px-4 py-3">
                      {e.success
                        ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-700">Geslaagd</span>
                        : <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-red-100 text-red-700">Mislukt</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-600">{e.ip}</td>
                    <td className="px-4 py-3 text-xs text-zinc-700">{e.device} · {e.browser}</td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-12 text-center text-sm text-zinc-500">Nog geen login events.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "sessions" && (
          <div className="card-flat overflow-x-auto" data-testid="cyber-sessions-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Gebruiker</th>
                  <th className="px-4 py-3 overline">Rol</th>
                  <th className="px-4 py-3 overline">Laatste login</th>
                  <th className="px-4 py-3 overline">IP / Device</th>
                  <th className="px-4 py-3 overline text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {sessions.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-sm text-zinc-500">Geen actieve sessies in de laatste 24u.</td></tr>
                ) : sessions.map((s) => (
                  <tr key={s.user_id} data-testid={`session-row-${s.user_id}`}>
                    <td className="px-4 py-3"><div className="font-medium text-[#1B2D5C]">{s.name}</div><div className="text-xs text-zinc-500 font-mono">{s.email}</div></td>
                    <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-zinc-100">{s.role}</span></td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(s.last_login).toLocaleString("nl-NL")}</td>
                    <td className="px-4 py-3 text-xs text-zinc-700"><code className="font-mono">{s.ip}</code> · {s.device} {s.browser}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => forceLogout(s.user_id, s.name)} className="inline-flex items-center gap-1 h-8 px-2 border border-red-200 hover:bg-red-50 text-red-600 rounded text-xs font-semibold" data-testid={`force-logout-${s.user_id}`}>
                        <UserX size={13} /> Force logout
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "docs" && (
          <div className="card-flat overflow-x-auto" data-testid="cyber-doc-log-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Wanneer</th>
                  <th className="px-4 py-3 overline">Gebruiker</th>
                  <th className="px-4 py-3 overline">Document</th>
                  <th className="px-4 py-3 overline">Categorie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {docLog.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-10 text-center text-sm text-zinc-500"><FileSearch size={28} className="mx-auto text-zinc-300 mb-2" />Nog geen documenten geopend.</td></tr>
                ) : docLog.map((d) => (
                  <tr key={d.id} data-testid={`doc-access-${d.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(d.created_at).toLocaleString("nl-NL")}</td>
                    <td className="px-4 py-3"><div className="font-medium">{d.user_name}</div><div className="text-xs text-zinc-500 font-mono">{d.user_email}</div></td>
                    <td className="px-4 py-3 font-medium">{d.document_name}</td>
                    <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-zinc-100">{d.category}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "audit" && (
          <div className="card-flat overflow-x-auto" data-testid="cyber-role-audit-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Wanneer</th>
                  <th className="px-4 py-3 overline">Actor</th>
                  <th className="px-4 py-3 overline">Actie</th>
                  <th className="px-4 py-3 overline">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {roleAudit.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-10 text-center text-sm text-zinc-500"><ClipboardList size={28} className="mx-auto text-zinc-300 mb-2" />Geen role/permission events.</td></tr>
                ) : roleAudit.map((r) => (
                  <tr key={r.id} data-testid={`role-audit-${r.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(r.created_at).toLocaleString("nl-NL")}</td>
                    <td className="px-4 py-3"><div className="font-medium">{r.actor_name}</div><div className="text-xs text-zinc-500">{r.actor_email}</div></td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-mono">{r.action}</span></td>
                    <td className="px-4 py-3 text-xs text-zinc-600 font-mono truncate max-w-md">{r.details ? JSON.stringify(r.details).slice(0, 100) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "feed" && <LiveFeed />}
      </div>
    </div>
  );
}

function OrgAnalyticsCard({ org }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-flat" data-testid={`org-analytics-${org.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-4 text-left">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#1B2D5C] tracking-tight">{org.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">Plan: {org.plan} · Laatste activiteit: {org.last_activity ? new Date(org.last_activity).toLocaleString("nl-NL") : "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-xs overline">Logins 7d</div>
          <div className="font-mono font-bold text-[#1B2D5C] text-lg">{org.logins_7d}</div>
        </div>
        <div className="text-right">
          <div className="text-xs overline">Retention</div>
          <div className="font-mono font-bold text-emerald-600 text-lg">{org.retention_score}</div>
        </div>
        {open ? <ChevronDown size={16} className="text-zinc-400" /> : <ChevronRight size={16} className="text-zinc-400" />}
      </button>
      {open && (
        <div className="border-t border-zinc-100 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="overline mb-2">Top gebruikers</div>
            {org.top_users.length === 0 ? <div className="text-xs text-zinc-400">Geen activiteit</div> : org.top_users.map((u, i) => (
              <div key={i} className="flex justify-between py-1 border-b border-zinc-50 last:border-0">
                <span>{u.name}</span>
                <span className="font-mono font-semibold">{u.logins} logins</span>
              </div>
            ))}
          </div>
          <div>
            <div className="overline mb-2">Apparaten</div>
            {Object.keys(org.devices).length === 0 ? <div className="text-xs text-zinc-400">Geen data</div> : Object.entries(org.devices).map(([dev, cnt]) => (
              <div key={dev} className="flex justify-between py-1 border-b border-zinc-50 last:border-0">
                <span className="inline-flex items-center gap-1.5">{deviceIcon(dev)} {dev}</span>
                <span className="font-mono font-semibold">{cnt}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function deviceIcon(d) {
  if (d === "iOS" || d === "Android") return <Smartphone size={13} className="text-zinc-500" />;
  if (d === "macOS" || d === "Windows" || d === "Linux") return <Laptop size={13} className="text-zinc-500" />;
  return <Monitor size={13} className="text-zinc-500" />;
}

function Kpi({ icon: Icon, label, value, accent, sub }) {
  const color = accent === "danger" ? "bg-red-50 text-red-600" : accent === "warn" ? "bg-amber-50 text-amber-600" : accent === "ok" ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-700";
  return (
    <div className="card-flat p-5">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${color}`}><Icon size={18} strokeWidth={1.8} /></div>
      <div className="overline">{label}</div>
      <div className="text-2xl sm:text-3xl font-bold tracking-tight mt-1 font-mono">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`h-10 px-4 rounded-md text-sm font-semibold transition-colors ${active ? "bg-[#1B2D5C] text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-[#5BC044]"}`} data-testid={testid}>{children}</button>
  );
}
